/**
 * MultiSplitTransactionTypeModal
 *
 * Collects a Transaction Type (and optional discount/credit percentages for Trading)
 * for each supplier split BEFORE the delivery-options API call is made.
 *
 * Rules (mirrored from TransactionTypeSelectComponent):
 * - Garaaz supplier → auto-select Trading, Facilitation option is disabled
 * - All order items are Maruti Suzuki → default to Facilitation
 * - Trading requires valid discountPercentage (0-100) and creditNotePercentage (≥0)
 * - Remaining-items split (supplierId=null) is never shown here
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Select,
  NumberInput,
  NumberInputField,
  Divider,
  Flex,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { MdContentCopy } from "react-icons/md";
import { TransactionType } from "../../views/Orders/enums";
import { isInvalidDiscountAndCredit } from "../../utils/util";
import {
  DeliveryOptionsSupplierInput,
  SupplierSplitTransactionTypeConfig,
} from "./types";

interface PerSplitRowState {
  supplierId: string;
  supplierName: string;
  transactionType: TransactionType | "";
  discountPercentage: string;
  creditNotePercentage: string;
  isFacilitationBlocked: boolean;
}

export interface MultiSplitTransactionTypeModalProps {
  isOpen: boolean;
  supplierSplits: DeliveryOptionsSupplierInput[];
  orderData: any;
  garaazSupplierIdFromConfig: string;
  onConfirm: (configs: SupplierSplitTransactionTypeConfig[]) => void;
  onCancel: () => void;
}

const DEFAULT_GARAAZ_SUPPLIER_ID = "64ae87c051c0be36170dc718";

function isGaraazSupplier(
  supplierId: string,
  garaazSupplierIdFromConfig: string,
): boolean {
  return (
    !!supplierId &&
    supplierId === (garaazSupplierIdFromConfig || DEFAULT_GARAAZ_SUPPLIER_ID)
  );
}

function isAllOrderItemsMarutiSuzuki(orderData: any): boolean {
  const items = orderData?.orderItems;
  if (!Array.isArray(items) || items.length === 0) return false;
  for (const item of items) {
    if (!item?.brand) return false;
    if (item.brand.trim().toLowerCase() !== "maruti suzuki") return false;
  }
  return true;
}

function resolveDefaultTransactionType(
  supplierId: string,
  garaazSupplierIdFromConfig: string,
  allOrderItemsMarutiSuzuki: boolean,
): TransactionType | "" {
  if (isGaraazSupplier(supplierId, garaazSupplierIdFromConfig)) {
    return TransactionType.TRADING;
  }
  if (allOrderItemsMarutiSuzuki) {
    return TransactionType.FACILITATION;
  }
  return "";
}

function buildInitialRowState(
  suppliers: DeliveryOptionsSupplierInput[],
  garaazSupplierIdFromConfig: string,
  allOrderItemsMarutiSuzuki: boolean,
): PerSplitRowState[] {
  return suppliers
    .filter((s) => s.supplierId != null)
    .map((s) => {
      const supplierId = s.supplierId as string;
      const facilitationBlocked = isGaraazSupplier(
        supplierId,
        garaazSupplierIdFromConfig,
      );
      return {
        supplierId,
        supplierName: s.supplierName,
        transactionType: resolveDefaultTransactionType(
          supplierId,
          garaazSupplierIdFromConfig,
          allOrderItemsMarutiSuzuki,
        ),
        discountPercentage: "",
        creditNotePercentage: "",
        isFacilitationBlocked: facilitationBlocked,
      };
    });
}

function isRowValid(row: PerSplitRowState): boolean {
  if (!row.transactionType) return false;
  if (row.transactionType === TransactionType.TRADING) {
    return !isInvalidDiscountAndCredit(
      Number(row.discountPercentage),
      Number(row.creditNotePercentage),
    );
  }
  return true;
}

function buildSupplierSplitTransactionTypeConfig(
  row: PerSplitRowState,
): SupplierSplitTransactionTypeConfig {
  const base: SupplierSplitTransactionTypeConfig = {
    supplierId: row.supplierId,
    transactionType: row.transactionType as TransactionType,
  };
  if (row.transactionType === TransactionType.TRADING) {
    return {
      ...base,
      discountPercentage: Number(row.discountPercentage),
      creditNotePercentage: Number(row.creditNotePercentage),
    };
  }
  return base;
}

interface SplitRowProps {
  row: PerSplitRowState;
  index: number;
  isFirst: boolean;
  onChange: (index: number, update: Partial<PerSplitRowState>) => void;
  onApplyToAll: (index: number) => void;
  rowCount: number;
}

const SplitRow: React.FC<SplitRowProps> = ({
  row,
  index,
  isFirst,
  onChange,
  onApplyToAll,
  rowCount,
}) => {
  const isTrading = row.transactionType === TransactionType.TRADING;
  const isValid = isRowValid(row);

  const handleTTChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(index, {
      transactionType: e.target.value as TransactionType | "",
    });
  };

  const handleDiscountChange = (valueAsString: string) => {
    const num = Number(valueAsString);
    if ((!isNaN(num) && num >= 0 && num <= 100) || valueAsString === "") {
      onChange(index, { discountPercentage: valueAsString });
    }
  };

  const handleCreditChange = (valueAsString: string) => {
    const num = Number(valueAsString);
    if ((!isNaN(num) && num >= 0 && num <= 100) || valueAsString === "") {
      onChange(index, { creditNotePercentage: valueAsString });
    }
  };

  return (
    <Box
      bg="white"
      py={2}
      px={2}
      borderRadius="md"
      position="relative"
      transition="all 0.2s"
      _hover={{ bg: "gray.50" }}
    >
      {/* Supplier name + Apply to All */}
      <Flex align="center" justify="space-between" mb={2}>
        <HStack spacing={1.5} maxW="70%" overflow="hidden">
          <Text
            fontSize="1rem"
            fontWeight="700"
            color="black"
            fontFamily="'Nunito','Inter', sans-serif"
            textTransform="uppercase"
            flexShrink={0}
          >
            {index + 1}.
          </Text>
          <Text
            fontWeight="700"
            fontSize="1rem"
            color="black"
            textTransform="uppercase"
            fontFamily="'Nunito','Inter', sans-serif"
            isTruncated
          >
            {row.supplierName}
          </Text>
        </HStack>

        {isFirst && rowCount > 1 && (
          <Tooltip
            label="Copy this transaction type to all other suppliers"
            placement="top"
            hasArrow
          >
            <Button
              leftIcon={<MdContentCopy size={14} />}
              size="xs"
              bg="blue.50"
              color="blue.600"
              _hover={{ bg: "blue.100", transform: "translateY(-1px)" }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
              onClick={() => onApplyToAll(index)}
              textTransform="uppercase"
              fontWeight="700"
            >
              APPLY TO ALL
            </Button>
          </Tooltip>
        )}
      </Flex>
      <HStack spacing={3} align="flex-start">
        <Box flex="1">
          <Text
            fontSize="0.9rem"
            fontWeight="700"
            color="black"
            mb={1}
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Type
          </Text>
          <Select
            value={row.transactionType}
            onChange={handleTTChange}
            borderRadius="md"
            fontFamily="'Nunito','Inter', sans-serif"
            fontSize="0.9rem"
            focusBorderColor="blue.400"
            bg="white"
          >
            <option value="">Select</option>
            {!row.isFacilitationBlocked && (
              <option value={TransactionType.FACILITATION}>Facilitation</option>
            )}
            <option value={TransactionType.TRADING}>Trading</option>
          </Select>
          {row.isFacilitationBlocked && (
            <Text
              fontSize="0.7rem"
              color="orange.500"
              mt={1}
              whiteSpace="nowrap"
            >
              Facilitation not available for Garaaz
            </Text>
          )}
        </Box>
        <Box flex="1">
          <Text
            fontSize="0.9rem"
            fontWeight="700"
            color={isTrading ? "black" : "gray.400"}
            mb={1}
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Discount %
          </Text>
          {isTrading ? (
            <InputGroup>
              <NumberInput
                min={0}
                max={100}
                value={row.discountPercentage}
                onChange={handleDiscountChange}
                focusBorderColor="blue.400"
                w="100%"
              >
                <NumberInputField
                  borderRadius="md"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontSize="0.9rem"
                  placeholder="0"
                />
              </NumberInput>
              <InputRightElement
                pointerEvents="none"
                color="gray.500"
                fontSize="0.9rem"
              >
                %
              </InputRightElement>
            </InputGroup>
          ) : (
            <Input
              value="N/A"
              isDisabled
              bg="gray.50"
              color="gray.400"
              borderRadius="md"
              fontFamily="'Nunito','Inter', sans-serif"
              fontSize="0.9rem"
            />
          )}
        </Box>
        <Box flex="1">
          <Text
            fontSize="0.9rem"
            fontWeight="700"
            color={isTrading ? "black" : "gray.400"}
            mb={1}
            fontFamily="'Nunito','Inter', sans-serif"
          >
            Credit %
          </Text>
          {isTrading ? (
            <InputGroup>
              <NumberInput
                min={0}
                max={100}
                value={row.creditNotePercentage}
                onChange={handleCreditChange}
                focusBorderColor="blue.400"
                w="100%"
              >
                <NumberInputField
                  borderRadius="md"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontSize="0.9rem"
                  placeholder="0"
                />
              </NumberInput>
              <InputRightElement
                pointerEvents="none"
                color="gray.500"
                fontSize="0.9rem"
              >
                %
              </InputRightElement>
            </InputGroup>
          ) : (
            <Input
              value="N/A"
              isDisabled
              bg="gray.50"
              color="gray.400"
              borderRadius="md"
              fontFamily="'Nunito','Inter', sans-serif"
              fontSize="0.9rem"
            />
          )}
        </Box>
      </HStack>
      {isTrading && !isValid && (
        <Text
          fontSize="0.7rem"
          color="orange.500"
          mt={2}
          fontFamily="'Nunito','Inter', sans-serif"
        >
          Enter valid Discount % (0–100) and Credit Note % (≥0) to proceed.
        </Text>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------

const MultiSplitTransactionTypeModal: React.FC<
  MultiSplitTransactionTypeModalProps
> = ({
  isOpen,
  supplierSplits,
  orderData,
  garaazSupplierIdFromConfig,
  onConfirm,
  onCancel,
}) => {
  const [rows, setRows] = useState<PerSplitRowState[]>([]);
  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  const allOrderItemsMarutiSuzuki = useMemo(
    () => isAllOrderItemsMarutiSuzuki(orderData),
    [orderData],
  );

  // Manual wheel event handler to force scrolling
  useEffect(() => {
    if (!isOpen) return;

    let cleanupFn: (() => void) | undefined;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const el = modalBodyRef.current;
      if (!el) return;

      const handleWheel = (e: WheelEvent) => {
        if (!e.defaultPrevented) {
          const maxScroll = el.scrollHeight - el.clientHeight;
          const newScrollTop = Math.max(
            0,
            Math.min(maxScroll, el.scrollTop + e.deltaY),
          );
          el.scrollTop = newScrollTop;
        }
      };

      el.addEventListener("wheel", handleWheel, { passive: false });

      cleanupFn = () => {
        el.removeEventListener("wheel", handleWheel);
      };
    }, 50);

    return () => {
      clearTimeout(timer);
      cleanupFn?.();
    };
  }, [isOpen]);

  // Re-initialise whenever the modal opens or the supplier list changes
  useEffect(() => {
    if (isOpen) {
      setRows(
        buildInitialRowState(
          supplierSplits,
          garaazSupplierIdFromConfig,
          allOrderItemsMarutiSuzuki,
        ),
      );
    }
  }, [
    isOpen,
    supplierSplits,
    garaazSupplierIdFromConfig,
    allOrderItemsMarutiSuzuki,
  ]);

  const allRowsValid = useMemo(() => rows.every(isRowValid), [rows]);

  const handleRowChange = useCallback(
    (index: number, update: Partial<PerSplitRowState>) => {
      setRows((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...update };
        // When switching away from Trading, clear the percentage fields
        if (
          update.transactionType &&
          update.transactionType !== TransactionType.TRADING
        ) {
          next[index].discountPercentage = "";
          next[index].creditNotePercentage = "";
        }
        return next;
      });
    },
    [],
  );

  const handleApplyToAll = useCallback((sourceIndex: number) => {
    setRows((prev) => {
      const source = prev[sourceIndex];
      return prev.map((row, i) => {
        if (i === sourceIndex) return row;
        // Respect the Garaaz supplier constraint
        if (
          row.isFacilitationBlocked &&
          source.transactionType === TransactionType.FACILITATION
        ) {
          return row; // Cannot override — leave as-is
        }
        return {
          ...row,
          transactionType: source.transactionType,
          discountPercentage: source.discountPercentage,
          creditNotePercentage: source.creditNotePercentage,
        };
      });
    });
  }, []);

  const handleConfirm = () => {
    const configs = rows.map(buildSupplierSplitTransactionTypeConfig);
    onConfirm(configs);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="lg"
      isCentered
      closeOnOverlayClick={false}
      blockScrollOnMount={true}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxH="85vh"
        borderRadius="xl"
        fontFamily="'Nunito','Inter', sans-serif"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Header */}
        <ModalHeader
          bg="white"
          py={4}
          px={5}
          borderBottom="1px solid"
          borderColor="gray.100"
          flexShrink={0}
        >
          <VStack align="start" spacing={1}>
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="black"
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Select Transaction Type
            </Text>
            <Text
              fontSize="0.9rem"
              color="gray.500"
              fontWeight="400"
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Please confirm the transaction type details for each supplier
              split.
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={1} />

        {/* Body */}
        <Box
          ref={modalBodyRef}
          bg="white"
          px={5}
          py={3}
          flex="1"
          overflowY="scroll"
          overflowX="hidden"
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#E2E8F0",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#A0AEC0",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#718096",
            },
          }}
        >
          <VStack spacing={4} align="stretch">
            {rows.map((row, i) => (
              <React.Fragment key={row.supplierId}>
                <SplitRow
                  row={row}
                  index={i}
                  isFirst={i === 0}
                  onChange={handleRowChange}
                  onApplyToAll={handleApplyToAll}
                  rowCount={rows.length}
                />
                {i < rows.length - 1 && <Divider borderColor="gray.200" />}
              </React.Fragment>
            ))}
          </VStack>

          {rows.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text color="gray.400" fontSize="0.9rem">
                No supplier splits to configure.
              </Text>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <ModalFooter
          bg="white"
          py={3}
          px={5}
          borderTop="1px solid"
          borderColor="gray.100"
          flexShrink={0}
        >
          <HStack spacing={3} justify="flex-end" w="100%">
            <Button
              variant="outline"
              onClick={onCancel}
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="600"
              color="black"
              borderColor="gray.200"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              bg="blue.500"
              color="white"
              fontFamily="'Nunito','Inter', sans-serif"
              fontWeight="600"
              isDisabled={!allRowsValid || rows.length === 0}
              onClick={handleConfirm}
              transition="all 0.2s"
              _hover={{
                bg: "blue.600",
                transform: "translateY(-1px)",
                shadow: "sm",
              }}
              _active={{ transform: "translateY(0)" }}
            >
              Confirm
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MultiSplitTransactionTypeModal;
